import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EmpresaProvider } from './contexts/EmpresaContext';
import { Toaster } from './components/ui/sonner';
import { setupAutoUpdate, APP_VERSION } from './config/version';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegistroPage from './pages/RegistroPage';
import RecuperarSenhaPage from './pages/RecuperarSenhaPage';
import RedefinirSenhaPage from './pages/RedefinirSenhaPage';

// Layouts
import PlataformaLayout from './layouts/PlataformaLayout';
import EmpresaLayout from './layouts/EmpresaLayout';
import EscritorioLayout from './layouts/EscritorioLayout';

// PWA App
import AppLayout from './pages/app/AppLayout';

// Unified App (G2EGENTE + Jornyx)
import UnifiedLayout from './pages/unified/UnifiedLayout';

// Empresa Ponto App (Totem com Reconhecimento Facial)
import EmpresaPontoLayout from './pages/empresa-ponto/EmpresaPontoLayout';

// Líder Login Page
import LiderLoginPage from './pages/lider/LiderLoginPage';

// Plataforma Pages
import PlataformaDashboard from './pages/plataforma/Dashboard';
import EmpresasPage from './pages/plataforma/Empresas';
import PlanosPage from './pages/plataforma/Planos';
import UsuariosPage from './pages/plataforma/Usuarios';
import SolicitacoesPage from './pages/plataforma/Solicitacoes';
import PlataformaConfiguracoes from './pages/plataforma/Configuracoes';
import ChatAdminPage from './pages/plataforma/Chat';
import PortalG2 from './pages/plataforma/PortalG2';
import ManutencaoPage from './pages/plataforma/Manutencao';

// Empresa Pages
import EmpresaDashboard from './pages/empresa/Dashboard';
import EmpregadosPage from './pages/empresa/Empregados';
import LideresPage from './pages/empresa/Lideres';
import EscalasPage from './pages/empresa/Escalas';
import EscalasCiclicasPage from './pages/empresa/EscalasCiclicas';
import DashboardGraficosPage from './pages/empresa/DashboardGraficos';
import MarcacoesPage from './pages/empresa/Marcacoes';
import ImportarAFDPage from './pages/empresa/ImportarAFD';
import BancoHorasPage from './pages/empresa/BancoHoras';
import HorasExtrasPage from './pages/empresa/HorasExtras';
import OcorrenciasPage from './pages/empresa/Ocorrencias';
import AtestadosPage from './pages/empresa/Atestados';
import NTEPPage from './pages/empresa/NTEP';
import RiscosPage from './pages/empresa/Riscos';
import EpidemiologiaPage from './pages/empresa/Epidemiologia';
import CartoesPontoPage from './pages/empresa/CartoesPonto';
import CartaoPontoPage from './pages/empresa/CartaoPonto';
import NotificacoesPage from './pages/empresa/Notificacoes';
import ConfiguracoesPage from './pages/empresa/Configuracoes';
import HorariosPage from './pages/empresa/Horarios';
import EscalasTrabalhoPage from './pages/empresa/EscalasTrabalho';

// Escritório Pages
import EscritorioDashboard from './pages/escritorio/Dashboard';
import EscritorioEmpresas from './pages/escritorio/Empresas';
import EscritorioAlertas from './pages/escritorio/Alertas';
import EscritorioRelatorios from './pages/escritorio/Relatorios';
import EscritorioConfiguracoes from './pages/escritorio/Configuracoes';
import EscritorioUsuarios from './pages/escritorio/Usuarios';

// Plataforma Pages - adicional
import EscritoriosAdminPage from './pages/plataforma/Escritorios';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedProfiles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedProfiles.length > 0 && !allowedProfiles.includes(user.perfil)) {
    // Redirect based on user profile
    if (user.perfil === 'administrador_plataforma') {
      return <Navigate to="/plataforma" replace />;
    }
    // Perfis de escritório vão para /escritorio
    if (['administrador_escritorio', 'analista_escritorio', 'auxiliar_escritorio'].includes(user.perfil)) {
      return <Navigate to="/escritorio" replace />;
    }
    return <Navigate to="/empresa" replace />;
  }

  return children;
};

// Public Route - Redirect if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    if (user.perfil === 'administrador_plataforma') {
      return <Navigate to="/plataforma" replace />;
    }
    // Perfis de escritório vão para /escritorio
    if (['administrador_escritorio', 'analista_escritorio', 'auxiliar_escritorio'].includes(user.perfil)) {
      return <Navigate to="/escritorio" replace />;
    }
    return <Navigate to="/empresa" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* PWA App - Controle de Ponto Mobile */}
      <Route path="/app/*" element={<AppLayout />} />

      {/* Unified App - G2EGENTE + Jornyx */}
      <Route path="/unified/*" element={<UnifiedLayout />} />

      {/* Empresa Ponto - Totem com Reconhecimento Facial */}
      <Route path="/empresa-ponto/*" element={<EmpresaPontoLayout />} />

      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/registro"
        element={
          <PublicRoute>
            <RegistroPage />
          </PublicRoute>
        }
      />
      <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />
      <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />

      {/* Líder Login - Acesso público */}
      <Route path="/lider-login" element={<LiderLoginPage />} />

      {/* Portal G2 - Fullscreen sem layout */}
      <Route
        path="/portal-g2"
        element={
          <ProtectedRoute allowedProfiles={['administrador_plataforma']}>
            <PortalG2 />
          </ProtectedRoute>
        }
      />

      {/* Platform Admin Routes */}
      <Route
        path="/plataforma"
        element={
          <ProtectedRoute allowedProfiles={['administrador_plataforma']}>
            <PlataformaLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PlataformaDashboard />} />
        <Route path="empresas" element={<EmpresasPage />} />
        <Route path="escritorios" element={<EscritoriosAdminPage />} />
        <Route path="planos" element={<PlanosPage />} />
        <Route path="usuarios" element={<UsuariosPage />} />
        <Route path="solicitacoes" element={<SolicitacoesPage />} />
        <Route path="chat" element={<ChatAdminPage />} />
        <Route path="configuracoes" element={<PlataformaConfiguracoes />} />
        <Route path="manutencao" element={<ManutencaoPage />} />
      </Route>

      {/* Company Routes */}
      <Route
        path="/empresa"
        element={
          <ProtectedRoute allowedProfiles={['administrador_empresa', 'rh', 'gestor', 'lider', 'encarregado', 'empregado', 'implantador', 'administrador_escritorio', 'analista_escritorio', 'auxiliar_escritorio']}>
            <EmpresaLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<EmpresaDashboard />} />
        <Route path="dashboard-graficos" element={<DashboardGraficosPage />} />
        <Route path="empregados" element={<EmpregadosPage />} />
        <Route path="lideres" element={<LideresPage />} />
        <Route path="escalas" element={<EscalasPage />} />
        <Route path="escalas-ciclicas" element={<EscalasCiclicasPage />} />
        <Route path="horarios" element={<HorariosPage />} />
        <Route path="escalas-trabalho" element={<EscalasTrabalhoPage />} />
        <Route path="marcacoes" element={<MarcacoesPage />} />
        <Route path="importar-afd" element={<ImportarAFDPage />} />
        <Route path="banco-horas" element={<BancoHorasPage />} />
        <Route path="horas-extras" element={<HorasExtrasPage />} />
        <Route path="ocorrencias" element={<OcorrenciasPage />} />
        <Route path="atestados" element={<AtestadosPage />} />
        <Route path="ntep" element={<NTEPPage />} />
        <Route path="riscos" element={<RiscosPage />} />
        <Route path="epidemiologia" element={<EpidemiologiaPage />} />
        <Route path="cartoes-ponto" element={<CartoesPontoPage />} />
        <Route path="cartao-ponto" element={<CartaoPontoPage />} />
        <Route path="notificacoes" element={<NotificacoesPage />} />
        <Route path="configuracoes" element={<ConfiguracoesPage />} />
      </Route>

      {/* Escritório Routes - Contabilidade */}
      <Route
        path="/escritorio"
        element={
          <ProtectedRoute allowedProfiles={['administrador_escritorio', 'analista_escritorio', 'auxiliar_escritorio']}>
            <EscritorioLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<EscritorioDashboard />} />
        <Route path="empresas" element={<EscritorioEmpresas />} />
        <Route path="usuarios" element={<EscritorioUsuarios />} />
        <Route path="alertas" element={<EscritorioAlertas />} />
        <Route path="relatorios" element={<EscritorioRelatorios />} />
        <Route path="configuracoes" element={<EscritorioConfiguracoes />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Configurar verificação automática de atualizações
  useEffect(() => {
    console.log(`[Jornyx] Versão ${APP_VERSION} iniciada`);
    const cleanup = setupAutoUpdate(5); // Verificar a cada 5 minutos
    return cleanup;
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <EmpresaProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </EmpresaProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
